import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { NavigateLinks, TextInput } from '@/components';
import { usersPageBreadcrumbs } from '../../config';
import { useUser } from '../../hooks';
import { Table } from '@/components';
import { assetsTableColumns } from '@/pages/Inventory/pages/Assets/config';
import { useGetTypesQuery } from '@/pages/Inventory/pages/Assets/Types/service';
import { useGetBrandsQuery } from '@/pages/Inventory/pages/Assets/Brands/service';
import { useGetAssetModelsQuery } from '@/pages/Inventory/pages/Assets/AssetModels/service';

import styles from './ViewUser.module.css';

const ViewUser = () => {
  const { userId } = useParams();
  if (!userId) throw new Error('El id del usuario es requerido');

  const navigate = useNavigate();
  const breadcrumbs = useMemo(() => usersPageBreadcrumbs(navigate), []);

  const { user, isLoading } = useUser({ userId });

  const { data: typesData } = useGetTypesQuery({
    paginated: { page: 1, pageSize: 1000 },
  });
  const { data: brandsData } = useGetBrandsQuery({
    paginated: { page: 1, pageSize: 1000 },
  });
  const { data: modelsData } = useGetAssetModelsQuery({
    paginated: { page: 1, pageSize: 1000 },
  });

  const userAssets = useMemo(() => {
    if (!user?.assets) return [];

    return user.assets.map((asset) => ({
      ...asset,
      assetType: {
        name:
          typesData?.items?.find((type) => type.id === asset.typeId)?.name ||
          `Tipo ID: ${asset.typeId?.slice(0, 8)}...`,
      },
      brand: {
        name:
          brandsData?.items?.find((brand) => brand.id === asset.brandId)
            ?.name || `Marca ID: ${asset.brandId?.slice(0, 8)}...`,
      },
      model: {
        name:
          modelsData?.items?.find((model) => model.id === asset.modelId)
            ?.name || `Modelo ID: ${asset.modelId?.slice(0, 8)}...`,
      },
    }));
  }, [user?.assets, typesData?.items, brandsData?.items, modelsData?.items]);

  const columns = useMemo(() => assetsTableColumns, []);

  const userColumns = useMemo(
    () =>
      columns.filter(
        (col) =>
          col.label !== 'Usuario actual' &&
          col.label !== 'Ubicación' &&
          col.label !== 'Estado'
      ),
    [columns]
  );

  return (
    <section className={styles.container}>
      <NavigateLinks
        actualPage={user?.fullName}
        breadcrumbs={breadcrumbs}
      />
      <div className={styles.content}>
        {isLoading ? (
          <p>Cargando...</p>
        ) : !user?.id ? (
          <div className={styles.error}>
            <h2>Usuario no encontrado</h2>
            <p>El usuario con ID {userId} no existe o no se pudo encontrar.</p>
            <button onClick={() => navigate('/inventory/users')}>
              Volver a la lista de usuarios
            </button>
          </div>
        ) : (
          <>
            <form className={styles.form}>
              <h1>Información del usuario</h1>
              <TextInput
                name='user-fullName'
                placeholder='Nombre completo'
                value={user?.fullName || ''}
                onChange={() => {}}
                disabled
              />
              <TextInput
                name='user-email'
                placeholder='Correo electrónico'
                value={user?.email || ''}
                onChange={() => {}}
                disabled
              />
              <TextInput
                name='user-phone'
                placeholder='Teléfono'
                value={user?.phone || ''}
                onChange={() => {}}
                disabled
              />
              <TextInput
                name='user-address'
                placeholder='Dirección'
                value={user?.address || ''}
                onChange={() => {}}
                disabled
              />
              <TextInput
                name='user-status'
                placeholder='Estado'
                value={
                  user?.status === 'disabled' ? 'Deshabilitado' : 'Habilitado'
                }
                onChange={() => {}}
                disabled
              />
            </form>
            <div className={styles.info}>
              <div className={styles.assetsSection}>
                <h3>Equipos asignados ({userAssets.length})</h3>
                {userAssets.length > 0 ? (
                  <div className={styles.userAssetsTable}>
                    <Table
                      isLoading={isLoading}
                      columns={userColumns}
                      data={userAssets}
                      skeletonRows={2}
                    />
                  </div>
                ) : (
                  <p>No hay equipos asignados</p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default ViewUser;
